import React, { useCallback, useContext, useState } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';

import EditInStudioOption from 'components/EditInStudioOption';
import useReportAnIssueModal from 'components/ReportAnIssue/useReportAnIssueModal';
import ShowOnMouseOverUnlessTour from 'components/ShowOnMouseOverUnlessTour';
import { useCanOpenStudio } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/UIVersionProvider';
import CurrentUserContext from 'contexts/CurrentUserContext';
import DashboardContext from 'contexts/DashboardContext';
import ReportContext from 'contexts/ReportContext';
import RolesContext from 'contexts/RolesContext';
import usePopup from 'hooks/usePopup';
import Row from 'components/Common/Row';
import Tooltip from 'components/Tooltip';
import Button from 'kingpin/atoms/Button';
import ContextMenu, { Option } from 'kingpin/atoms/ContextMenu';
import PERMISSIONS from 'permissionDefinitions';

import CompositeFiltersModal from './CompositeFiltersModal';
import FullscreenBlockButton from './FullscreenBlockButton';

// A Composite block's header buttons on a dashboard or report: on a
// dashboard, its dates for the view (see StudioCompositeCardView), a filters
// icon (gated as a gadget's Edit Filters, or a report gadget's Edit, are), a
// fullscreen icon and a ⋮ menu: Edit (in Studio, for those who can edit it,
// wherever the UI has Studio) and, for FleetOps staff, issue reports. It
// has no gadget doc, so none of a gadget's copy or raw data options.
const CompositeMenuButtons = ({
  card,
  datesButton,
  onFullscreen,
}: {
  card: CanvasCard.StudioCompositeCard;
  // Before the filters icon; shows and hides itself on hover
  datesButton?: JSX.Element;
  onFullscreen: () => void;
}) => {
  const { dashboard, templatedFrom } = useContext(DashboardContext);
  const { report } = useContext(ReportContext);
  const { currentPermissions } = useContext(RolesContext);
  const { isFleetOpsStaff } = useContext(CurrentUserContext);
  const canOpenStudio = useCanOpenStudio();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const onMenuOpen = useCallback(() => setIsMenuOpen(true), []);
  const onMenuClose = useCallback(() => setIsMenuOpen(false), []);
  const filtersPopup = usePopup();
  const { open: openReportAnIssue, modal: reportAnIssueModal } =
    useReportAnIssueModal({
      type: report ? 'report-gadget' : 'dashboard-gadget',
      gadgetName: card.content.composite.name,
    });

  const has = (permission: FleetOps.Permission) =>
    currentPermissions.includes(permission);
  const isEditableDashboard =
    !report &&
    !!dashboard &&
    !templatedFrom &&
    has(PERMISSIONS.DASHBOARDS.EDIT);
  const canEditFilters = report
    ? has(PERMISSIONS.REPORT_MANAGEMENT.ADD_REMOVE_VISUALISATIONS)
    : isEditableDashboard;
  const studioSource = (():
    | { contentType: Studio.ContentType; id: string }
    | undefined => {
    if (!canOpenStudio) {
      return undefined;
    }
    if (report) {
      return has(PERMISSIONS.REPORT_MANAGEMENT.EDIT_LAYOUT)
        ? { contentType: 'report', id: report.id }
        : undefined;
    }
    return isEditableDashboard && dashboard
      ? { contentType: 'dashboard', id: dashboard.id }
      : undefined;
  })();

  return (
    <Row centerAlign>
      {datesButton}
      {canEditFilters && (
        <ShowOnMouseOverUnlessTour>
          <div style={{ marginRight: 4 }}>
            <Tooltip content="Filters" position="top">
              <Button
                type="Ghost"
                size="Small"
                icon="filters"
                onClick={filtersPopup.open}
                testId="composite-filters-open"
              />
            </Tooltip>
          </div>
        </ShowOnMouseOverUnlessTour>
      )}
      <FullscreenBlockButton onClick={onFullscreen} />
      {(isFleetOpsStaff || studioSource) && (
        <ShowOnMouseOverUnlessTour isForcedVisible={isMenuOpen}>
          <ContextMenu
            buttonType="Ghost"
            onOpen={onMenuOpen}
            onClose={onMenuClose}
            testId="studio-composite-menu"
          >
            <>
              {studioSource && (
                <EditInStudioOption
                  contentType={studioSource.contentType}
                  id={studioSource.id}
                  blockId={card.layout.i}
                />
              )}
              {isFleetOpsStaff && (
                <Option
                  label="Report an issue"
                  onClick={openReportAnIssue}
                  testId="report-an-issue"
                />
              )}
            </>
          </ContextMenu>
        </ShowOnMouseOverUnlessTour>
      )}
      <ModalTransition>
        {filtersPopup.isOpen && (
          <CompositeFiltersModal card={card} close={filtersPopup.close} />
        )}
      </ModalTransition>
      {reportAnIssueModal}
    </Row>
  );
};

export default CompositeMenuButtons;
